import { IConformationRegularBreedAwards, IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { preferScores } from "./labels";
import { IClassSection, LicensedStream } from "./types";

const LEVEL = Constants.dog_class_element_level;
const ELEMENT = Constants.dog_class_element;

/** Licensed class levels that get 1st–4th by sex (and by variety when present). */
export const LICENSED_CLASS_LEVELS: string[] = [
  LEVEL.puppy,
  LEVEL.junior,
  LEVEL.adult,
  LEVEL.open,
  LEVEL.breader_handler,
];

export function licensedShowElement(stream: LicensedStream): string {
  return stream === "altered" ? ELEMENT.conformation_altered : ELEMENT.conformation;
}

/** Class dogs: everyone except Champion / Grand Champion. */
export function classDogs(scores: IScore[]): IScore[] {
  return scores.filter((score) => LICENSED_CLASS_LEVELS.includes(score.dog_class?.level || ""));
}

export function varietyOf(score: IScore): string {
  return (score.variety || "").trim();
}

/** Distinct, sorted variety names among class dogs of this breed. */
export function varietyNames(scores: IScore[]): string[] {
  return Array.from(new Set(classDogs(scores).map(varietyOf).filter(Boolean))).sort();
}

/**
 * Class placement sections for a licensed breed card: one per level × sex × variety
 * that has at least one dog. Breeds with varieties also get sections for dogs with no
 * variety set, after the named varieties.
 */
export function buildClassSections(scores: IScore[]): IClassSection[] {
  const dogs = classDogs(scores);
  const varieties = varietyNames(scores);
  const varietyKeys = varieties.length ? varieties : [""];
  const sections: IClassSection[] = [];

  varietyKeys.forEach((variety) => {
    LICENSED_CLASS_LEVELS.forEach((level) => {
      (["Male", "Female"] as const).forEach((sex) => {
        const hasDog = dogs.some(
          (score) =>
            score.dog_class?.level === level &&
            score.dog_sex === sex &&
            varietyOf(score) === variety
        );
        if (hasDog) sections.push({ level, sex, variety });
      });
    });
  });

  if (varieties.length) {
    LICENSED_CLASS_LEVELS.forEach((level) => {
      (["Male", "Female"] as const).forEach((sex) => {
        const hasUnspecified = dogs.some(
          (score) =>
            score.dog_class?.level === level && score.dog_sex === sex && !varietyOf(score)
        );
        if (hasUnspecified) sections.push({ level, sex, variety: "" });
      });
    });
  }

  return sections;
}

export function classSectionKey(section: IClassSection): string {
  return `${section.level}|${section.sex}|${section.variety}`;
}

export function classSectionDogs(scores: IScore[], section: IClassSection): IScore[] {
  return classDogs(scores).filter(
    (score) =>
      score.dog_class?.level === section.level &&
      score.dog_sex === section.sex &&
      varietyOf(score) === section.variety
  );
}

export interface ILicensedPools {
  males: IScore[];
  females: IScore[];
  classDogs: IScore[];
  champions: IScore[];
  grandChampions: IScore[];
  /** Best Male / Reserve: class males, class 1sts first. */
  bestMale: IScore[];
  /** Best Female / Reserve: class females, class 1sts first. */
  bestFemale: IScore[];
  /** Best of Winners: class dogs, BM/BF first. */
  bestOfWinners: IScore[];
  /** Best of Breed: every dog in the stream, GRCH / CH / BOW first. */
  bestOfBreed: IScore[];
  /** Per variety: class males / females of that variety. */
  varieties: Array<{ variety: string; males: IScore[]; females: IScore[] }>;
}

/**
 * Candidate pools for the licensed award ladder, ordered the way the web card
 * orders them (saved / implied winners first, then armband).
 */
export function licensedPools(
  scores: IScore[],
  placementsBySection: Record<string, string[]>,
  awards: IConformationRegularBreedAwards
): ILicensedPools {
  const dogs = classDogs(scores);
  const males = dogs.filter((score) => score.dog_sex === "Male");
  const females = dogs.filter((score) => score.dog_sex === "Female");
  const champions = scores.filter((score) => score.dog_class?.level === LEVEL.champion);
  const grandChampions = scores.filter(
    (score) => score.dog_class?.level === LEVEL.grand_champion
  );
  const firstPlaceIds = Object.values(placementsBySection).map((row) => row[0]);

  return {
    males,
    females,
    classDogs: dogs,
    champions,
    grandChampions,
    bestMale: preferScores(males, firstPlaceIds),
    bestFemale: preferScores(females, firstPlaceIds),
    bestOfWinners: preferScores(dogs, [awards.best_male, awards.best_female]),
    bestOfBreed: preferScores(scores, [
      awards.grand_champion,
      awards.champion,
      awards.best_of_winners,
    ]),
    varieties: varietyNames(scores).map((variety) => ({
      variety,
      males: males.filter((score) => varietyOf(score) === variety),
      females: females.filter((score) => varietyOf(score) === variety),
    })),
  };
}

export interface INonLicensedPools {
  males: IScore[];
  females: IScore[];
  /** Best of Breed: all dogs, the two class 1sts first. */
  bestOfBreed: IScore[];
}

/** Novice Puppy / Veteran: Male 1st–4th, Female 1st–4th, BOB. */
export function nonLicensedPools(
  scores: IScore[],
  malePlacements: string[],
  femalePlacements: string[]
): INonLicensedPools {
  const males = scores.filter((score) => score.dog_sex === "Male");
  const females = scores.filter((score) => score.dog_sex === "Female");
  return {
    males,
    females,
    bestOfBreed: preferScores(scores, [malePlacements[0], femalePlacements[0]]),
  };
}

/** Brace: one 1st–4th list, Best Brace prefers the 1st. */
export function bracePools(
  scores: IScore[],
  placements: string[]
): { placements: IScore[]; bestBrace: IScore[] } {
  return {
    placements: scores,
    bestBrace: preferScores(scores, [placements[0]]),
  };
}
